// RITA-1547 — Rückfragen zum Entwurf „Vertrag erstellen“ (Bildentwurf 3b, freigegeben von Julia am
// 20.10.2026): B „Es gibt schon einen Entwurf“, D „Entwurf verwerfen?“, E „Seit dem Entwurf hat sich etwas
// geändert“, F „Seite verlassen?“ und G „Entwurf wurde inzwischen gespeichert/verworfen“. Die Knöpfe stehen
// in der freigegebenen Reihenfolge; der empfohlene Weg ist ausgefüllt. Die Aufrufer hängen jede Rückfrage
// nur ein, solange sie offen ist (wie das Fenster „Vertrag erstellen“), damit beim Schließen kein leerer
// oder vertauschter Text aufblitzt.
//
// RITA-1552 (Skizze Rücklauf, Bild D a; Festlegung Julia 22.10.2026): Die Rückfrage B gibt es auch für den eigenen
// Entwurf der Rücksendung – im 3b-Muster mit demselben Titel und denselben Knöpfen, der Text nennt „der Rücksendung“.
import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import type { Konflikt } from '../hooks/useEntwurfSpeichern';
import type { Vorgang } from '../services/vorgang';
import {
  ABBRECHEN,
  ENTWURF_BEHALTEN,
  ENTWURF_SPEICHERN,
  GEAENDERT_HINWEIS,
  GEAENDERT_TITEL,
  GELOESCHT_HINWEIS,
  GESPEICHERTEN_STAND_LADEN,
  konfliktHinweis,
  konfliktText,
  KONFLIKT_TITEL,
  NEU_BEGINNEN,
  OK,
  TROTZDEM_SPEICHERN,
  VERLASSEN_TEXT,
  VERLASSEN_TITEL,
  VERWERFEN,
  verwerfenNeuerStandText,
  verwerfenText,
  VERWERFEN_TITEL,
  VERWORFEN_HINWEIS,
  VERWORFEN_TEXT,
  VERWORFEN_TITEL,
  VORHANDEN_HINWEIS,
  vorhandenText,
  VORHANDEN_TITEL,
  WEITER_BEARBEITEN,
  WEITERBEARBEITEN,
  WIRD_GESPEICHERT,
  ZURUECK_ZUM_BERATER,
} from '../services/vertragsentwurfTexte';

/** Knopftexte wie freigegeben schreiben; das App-Design setzt sonst jedes Wort groß. */
const KNOEPFE = { '& .MuiButton-root': { textTransform: 'none' } } as const;

interface Entwurfskopf {
  updated_at: string;
  updated_by_name: string | null;
}

// Bild B
export function EntwurfVorhandenDialog({
  entwurf,
  vorgang = 'contract',
  onAbbrechen,
  onNeuBeginnen,
  onWeiterbearbeiten,
}: {
  entwurf: Entwurfskopf;
  /** RITA-1552: Entwurf des Vertrags (3b) oder der Rücksendung („Es gibt einen Entwurf der Rücksendung vom …“). */
  vorgang?: Vorgang;
  onAbbrechen: () => void;
  onNeuBeginnen: () => void;
  onWeiterbearbeiten: () => void;
}) {
  return (
    <Dialog open onClose={onAbbrechen} aria-labelledby="entwurf-vorhanden-titel">
      <DialogTitle id="entwurf-vorhanden-titel">{VORHANDEN_TITEL}</DialogTitle>
      <DialogContent>
        <DialogContentText data-testid="entwurf-vorhanden-text">
          {vorhandenText(entwurf.updated_at, entwurf.updated_by_name, vorgang)}
        </DialogContentText>
        <Typography sx={{ mt: 1.5, fontSize: 13, color: 'text.secondary' }}>{VORHANDEN_HINWEIS}</Typography>
      </DialogContent>
      <DialogActions sx={KNOEPFE}>
        <Button onClick={onAbbrechen}>{ABBRECHEN}</Button>
        <Button variant="outlined" onClick={onNeuBeginnen}>
          {NEU_BEGINNEN}
        </Button>
        <Button variant="contained" onClick={onWeiterbearbeiten}>
          {WEITERBEARBEITEN}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Bild D (Rückfrage)
export function EntwurfVerwerfenDialog({
  entwurf,
  neuerStand,
  verwirft,
  fehler,
  onAbbrechen,
  onVerwerfen,
}: {
  entwurf: Entwurfskopf;
  /** Verwerfen traf auf einen inzwischen neu gespeicherten Stand; die Rückfrage zeigt ihn. */
  neuerStand: boolean;
  verwirft: boolean;
  fehler: string;
  onAbbrechen: () => void;
  onVerwerfen: () => void;
}) {
  return (
    <Dialog open onClose={verwirft ? undefined : onAbbrechen} aria-labelledby="entwurf-verwerfen-titel">
      <DialogTitle id="entwurf-verwerfen-titel">{VERWERFEN_TITEL}</DialogTitle>
      <DialogContent>
        {neuerStand && (
          <Alert severity="warning" sx={{ mb: 1.5 }} data-testid="entwurf-verwerfen-neuer-stand">
            {verwerfenNeuerStandText(entwurf.updated_by_name)}
          </Alert>
        )}
        <DialogContentText data-testid="entwurf-verwerfen-text">
          {verwerfenText(entwurf.updated_at, entwurf.updated_by_name)}
        </DialogContentText>
        {fehler && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {fehler}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={KNOEPFE}>
        <Button onClick={onAbbrechen} disabled={verwirft}>
          {ABBRECHEN}
        </Button>
        <Button variant="contained" color="error" onClick={onVerwerfen} disabled={verwirft}>
          {VERWERFEN}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export interface Aenderungshinweis {
  /** Zeilen wie „Die Vorlage „…“ wurde am … geändert.“ und „Berater-Daten: Straße, Ort“. */
  zeilen: string[];
  /** Eine Vorlage gibt es nicht mehr. */
  geloescht: boolean;
}

// Bild E
export function EntwurfGeaendertDialog({
  hinweis,
  keineVorlage,
  laedt,
  onBehalten,
  onNeuBeginnen,
}: {
  hinweis: Aenderungshinweis;
  /** Meldung (c): „Neu beginnen“ fand keine passende Vorlage; der Entwurf bleibt. */
  keineVorlage: string | null;
  laedt: boolean;
  onBehalten: () => void;
  onNeuBeginnen: () => void;
}) {
  return (
    <Dialog open aria-labelledby="entwurf-geaendert-titel">
      <DialogTitle id="entwurf-geaendert-titel">{GEAENDERT_TITEL}</DialogTitle>
      <DialogContent>
        {keineVorlage ? (
          <Alert severity="warning" data-testid="entwurf-keine-vorlage">
            {keineVorlage}
          </Alert>
        ) : (
          <>
            <Box component="ul" sx={{ my: 0, pl: 2.5 }} data-testid="entwurf-geaendert-liste">
              {hinweis.zeilen.map((zeile) => (
                <li key={zeile}>{zeile}</li>
              ))}
            </Box>
            {hinweis.geloescht && (
              <Typography sx={{ mt: 1.5, fontSize: 13, color: 'text.secondary' }}>{GELOESCHT_HINWEIS}</Typography>
            )}
            {(!hinweis.geloescht || hinweis.zeilen.length > 1) && (
              <Typography sx={{ mt: 1.5, fontSize: 13, color: 'text.secondary' }}>{GEAENDERT_HINWEIS}</Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={KNOEPFE}>
        {keineVorlage ? (
          <Button variant="contained" onClick={onBehalten}>
            {OK}
          </Button>
        ) : (
          <>
            <Button variant="outlined" onClick={onNeuBeginnen} disabled={laedt}>
              {NEU_BEGINNEN}
            </Button>
            <Button variant="contained" onClick={onBehalten} disabled={laedt}>
              {ENTWURF_BEHALTEN}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

// Bild F
export function SeiteVerlassenDialog({
  speichert,
  onWeiterBearbeiten,
  onVerwerfen,
  onSpeichern,
}: {
  speichert: boolean;
  onWeiterBearbeiten: () => void;
  onVerwerfen: () => void;
  onSpeichern: () => void;
}) {
  return (
    <Dialog open onClose={speichert ? undefined : onWeiterBearbeiten} aria-labelledby="seite-verlassen-titel">
      <DialogTitle id="seite-verlassen-titel">{VERLASSEN_TITEL}</DialogTitle>
      <DialogContent>
        <DialogContentText>{VERLASSEN_TEXT}</DialogContentText>
      </DialogContent>
      <DialogActions sx={KNOEPFE}>
        <Button onClick={onWeiterBearbeiten} disabled={speichert}>
          {WEITER_BEARBEITEN}
        </Button>
        <Button variant="outlined" color="error" onClick={onVerwerfen} disabled={speichert}>
          {VERWERFEN}
        </Button>
        <Button variant="contained" onClick={onSpeichern} disabled={speichert}>
          {speichert ? WIRD_GESPEICHERT : ENTWURF_SPEICHERN}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Bild G
export function EntwurfKonfliktDialog({
  konflikt,
  speichert,
  trotzdemMoeglich = true,
  onStandLaden,
  onZurueckZumBerater,
  onTrotzdemSpeichern,
}: {
  konflikt: Konflikt;
  speichert: boolean;
  /** „Trotzdem speichern“ braucht die Revision aus der Warnung. */
  trotzdemMoeglich?: boolean;
  onStandLaden: () => void;
  onZurueckZumBerater: () => void;
  onTrotzdemSpeichern: () => void;
}) {
  const verworfen = konflikt.art === 'verworfen';
  return (
    <Dialog open aria-labelledby="entwurf-konflikt-titel">
      <DialogTitle id="entwurf-konflikt-titel">{verworfen ? VERWORFEN_TITEL : KONFLIKT_TITEL}</DialogTitle>
      <DialogContent>
        <DialogContentText data-testid="entwurf-konflikt-text">
          {konflikt.art === 'gespeichert' ? konfliktText(konflikt.von, konflikt.am) : VERWORFEN_TEXT}
        </DialogContentText>
        <Typography sx={{ mt: 1.5, fontSize: 13, color: 'text.secondary' }}>
          {konflikt.art === 'gespeichert' ? konfliktHinweis(konflikt.von) : VERWORFEN_HINWEIS}
        </Typography>
      </DialogContent>
      <DialogActions sx={KNOEPFE}>
        {verworfen ? (
          <>
            <Button onClick={onZurueckZumBerater} disabled={speichert}>
              {ZURUECK_ZUM_BERATER}
            </Button>
            <Button variant="contained" onClick={onTrotzdemSpeichern} disabled={speichert}>
              {speichert ? WIRD_GESPEICHERT : TROTZDEM_SPEICHERN}
            </Button>
          </>
        ) : (
          <>
            <Button variant="contained" onClick={onStandLaden} disabled={speichert}>
              {GESPEICHERTEN_STAND_LADEN}
            </Button>
            <Button variant="outlined" onClick={onTrotzdemSpeichern} disabled={speichert || !trotzdemMoeglich}>
              {speichert ? WIRD_GESPEICHERT : TROTZDEM_SPEICHERN}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
